import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../api/client';
import endpoints from '../api/endpoints';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get(endpoints.auth.me);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      // Clear all auth and chat data if token is invalid
      localStorage.removeItem('token');
      localStorage.removeItem('nickChatHistory');
      localStorage.removeItem('nickCurrentThread');
      localStorage.removeItem('nickIsNewThread');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post(endpoints.auth.login, {
        username,
        password,
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      
      toast.success(`Welcome back, ${user.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post(endpoints.auth.register, userData);

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      
      toast.success(`Welcome to Nick, ${user.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    // Remove authentication token
    localStorage.removeItem('token');
    
    // Clear chat history to prevent next user from seeing previous conversations
    localStorage.removeItem('nickChatHistory');
    localStorage.removeItem('nickCurrentThread');
    localStorage.removeItem('nickIsNewThread');
    
    setUser(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
